import { toTenantDateStr } from '../date/tenantDate';

/** AI credits at or above this share of the allowance get the amber heads-up (KAN-726; same 80% as platform KAN-727). */
export const AI_HEADS_UP_RATIO = 0.8;

const dayNumber = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000) : null;
};

/** Whole calendar days from `fromYmd` to `toYmd` (negative = already past); null when unparseable. */
export function daysBetween(fromYmd, toYmd) {
  const a = dayNumber(fromYmd);
  const b = dayNumber(toYmd);
  return a === null || b === null ? null : b - a;
}

/**
 * Calendar day for a date or timestamp. A DATE column (`trial_ends_at`,
 * `past_due_since`, `resets_on`) arrives as a bare 'YYYY-MM-DD' and IS the
 * company's day already — it is returned as-is, never time-zone converted
 * (`new Date('2026-10-18')` is UTC midnight, which shifted every US company a
 * day early — QA 2026-10-09). Only real timestamps are converted to the company zone.
 */
export const toCompanyDay = (value, tz) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return toTenantDateStr(value, tz);
};

/**
 * The AI meter: null when AI is off. Unlimited (null allowance) has no meter.
 * `used`/`allowance` are credits only — never dollars.
 */
export function aiMeter(plan) {
  const ai = plan?.ai;
  if (!ai || !ai.enabled) return null;
  if (ai.allowance === null || ai.allowance === undefined) return { unlimited: true, used: ai.usedThisMonth };
  const allowance = Number(ai.allowance);
  const used = Number(ai.usedThisMonth) || 0;
  if (!(allowance > 0)) return { unlimited: false, used, allowance, percent: used > 0 ? 100 : 0, left: 0, tone: 'danger' };
  const ratio = used / allowance;
  const percent = Math.min(100, Math.floor(ratio * 100));
  const tone = ratio >= 1 ? 'danger' : ratio >= AI_HEADS_UP_RATIO ? 'warning' : 'normal';
  return { unlimited: false, used, allowance, percent, left: Math.max(0, allowance - used), tone, ratio };
}

/**
 * The dashboard heads-up pill (KAN-726): null below 80%, amber 80–99%, red at
 * 100%+. An unlimited plan, AI off, or no allowance never shows it.
 */
export function aiHeadsUp(plan) {
  const m = aiMeter(plan);
  if (!m || m.unlimited || !(m.allowance > 0)) return null;
  if (m.tone === 'normal') return null;
  return { tone: m.tone === 'danger' ? 'danger' : 'warning', percent: m.percent, usedUp: m.tone === 'danger' };
}
